import { useHandleError } from "@/hooks/useHandleError";
import { usePostPointWithdrawalRequest } from "@/hooks/usePostPointWithdrawal";
import { getAuthenticatedUser } from "@/store/auth.store";
import type { PointWithdrawalType } from "@/types/PointWithdrawal";
import { yenToPoints } from "@/utils/pointsToYen";
import { useState } from "react";
import { NeedToSMSVerifyModal } from "./NeedToSMSVerifyModal";
import { PaymentForm } from "./Payment";
import { WithdrawalConfirmationPage } from "./Payment/Confirmation";
import { WithDrawalPage } from "./PaymentMethod";
import { WithdrawalSuccessPage } from "./Success";
import { METHOD_CONTENT } from "./constant";

export enum PaymentHeading {
  Amazon = "Amazonギフトカード",
  Apple = "Apple Gift Card",
  Google = "Google Playギフトカード",
  Selective = "えらべるPay",
  Giftee = "Giftee Box",
}

enum PageStep {
  payment = "PaymentMethod",
  form = "Form",
  confirmation = "Confirmation",
  success = "Success",
}

export type CardQuantity = {
  yenOption: string;
  quantity: number;
};

export type MethodContent = {
  method: PointWithdrawalType;
  exchangeRange: string;
  availableYenOptions: string[];
  description: string;
  iconSrc: string;
  heading: string;
};

export const WithdrawalHome = () => {
  const [cardQuantities, setCardQuantities] = useState<CardQuantity[]>([]);
  const [methodContent, setMethodContent] = useState<MethodContent | null>(null);
  const [step, setStep] = useState<PageStep>(PageStep.payment);

  const handleError = useHandleError("出金に失敗しました");
  const { data: pointWithdrawalId, trigger } = usePostPointWithdrawalRequest(handleError);

  const handlePaymentMethod = ({
    selectedMethod,
  }: {
    selectedMethod: PointWithdrawalType;
  }) => {
    const content = METHOD_CONTENT.find((methodContent: MethodContent) => methodContent.method === selectedMethod);
    if (content) {
      setMethodContent(content);
    }
    setStep(PageStep.form);
  };

  const handleFormConfirming = () => {
    setStep(PageStep.confirmation);
  };

  const handleConfirmation = async () => {
    if (!methodContent) {
      throw new Error("Method is not selected");
    }

    // Validate inputs
    const totalPoints = cardQuantities.reduce((acc, { yenOption, quantity }) => {
      const yen = Number(yenOption);
      if (Number.isNaN(yen) || yen <= 0) {
        throw new Error(`Invalid yen amount: ${yenOption}`);
      }
      if (!Number.isInteger(quantity) || quantity <= 0) {
        throw new Error(`Invalid quantity: ${quantity}`);
      }
      return acc + yenToPoints(yen * quantity);
    }, 0);

    if (methodContent.method === "Apple" && totalPoints < 10000) {
      throw new Error("Apple Payでは1000円以下の出金はできません");
    }

    await trigger({
      type: methodContent.method,
      redeemablePoints: totalPoints.toString(),
    });
    setStep(PageStep.success);
  };

  const user = getAuthenticatedUser();

  if (!user.isSMSVerified) {
    return <NeedToSMSVerifyModal />;
  }

  return (
    <div>
      {step === "PaymentMethod" && <WithDrawalPage onClick={handlePaymentMethod} />}
      {step === "Form" && (
        <PaymentForm
          methodContent={methodContent}
          cardQuantities={cardQuantities}
          setCardQuantities={setCardQuantities}
          backButtonClick={() => {
            setStep(PageStep.payment);
            setCardQuantities([]);
          }}
          onClick={handleFormConfirming}
        />
      )}
      {step === "Confirmation" && methodContent && (
        <WithdrawalConfirmationPage
          methodContent={methodContent}
          cardQuantities={cardQuantities}
          backButtonClick={() => setStep(PageStep.form)}
          onClick={handleConfirmation}
        />
      )}
      {step === "Success" && pointWithdrawalId && (
        <WithdrawalSuccessPage
          id={pointWithdrawalId}
          amount={cardQuantities.reduce((acc, { yenOption, quantity }) => acc + Number(yenOption) * quantity, 0)}
        />
      )}
    </div>
  );
};
